import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useGuidedUpdate, type GuidedView } from '@/hooks/useGuidedUpdate';

const OFFER_TEXT =
  'Nouvelle version de votre caisse (monnayeur automatique intégré, impression plus rapide). 1 minute, tout est conservé.';

function messageFor(view: GuidedView): string {
  switch (view.phase) {
    case 'offer':
      return OFFER_TEXT;
    case 'downloading':
      return view.percent !== null
        ? `Téléchargement de la nouvelle version… ${view.percent} %`
        : 'Téléchargement de la nouvelle version…';
    case 'ready':
      return view.canClose
        ? 'Prête : l’appli va se fermer dans quelques secondes. Rouvrez VisionPOS puis appuyez sur « Installer ».'
        : 'Prête : fermez puis rouvrez VisionPOS, puis appuyez sur « Installer ».';
    case 'restart':
      return view.canClose
        ? 'Pour terminer, l’appli va se fermer dans quelques secondes. Rouvrez VisionPOS puis appuyez sur « Installer » si Android le demande.'
        : 'Pour terminer, fermez puis rouvrez VisionPOS, puis appuyez sur « Installer » si Android le demande.';
    case 'installing':
      return 'Appuyez sur « Installer » sur l’écran Android, puis rouvrez VisionPOS.';
    case 'blocked':
      return 'Le téléchargement automatique n’aboutit pas sur cette caisse. Ouvrez le volet des notifications Android : si « VisionPOS 360 — Mise à jour » y figure, appuyez dessus puis sur « Installer ». Sinon, ouvrez Chrome sur la caisse à l’adresse pos.visionpos.fr/android et appuyez sur « Télécharger », ou appelez le support.';
    default:
      return '';
  }
}

/**
 * Proposition de mise à jour des anciennes caisses Android (< 1.5.9.33). Bandeau non
 * bloquant (pas une fenêtre modale) : il ne gêne pas l'encaissement et ne compte pas
 * comme « fenêtre ouverte » pour la détection de caisse au repos.
 */
export function GuidedUpdateBanner() {
  const { view, accept, dismiss } = useGuidedUpdate();
  if (view.phase === 'hidden') return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-1/2 top-3 z-[1000] w-[min(92vw,34rem)] -translate-x-1/2 rounded-lg border bg-background p-4 shadow-2xl"
    >
      <div className="flex items-start gap-3">
        <Download className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1 space-y-3">
          <p className="text-sm font-semibold">Mise à jour de la caisse</p>
          <p className="text-sm text-muted-foreground">{messageFor(view)}</p>
          <GuidedUpdateActions phase={view.phase} onAccept={accept} onDismiss={dismiss} />
        </div>
      </div>
    </div>
  );
}

type GuidedUpdateActionsProps = {
  phase: GuidedView['phase'];
  onAccept: () => void;
  onDismiss: () => void;
};

function GuidedUpdateActions({ phase, onAccept, onDismiss }: GuidedUpdateActionsProps) {
  if (phase === 'offer') {
    return (
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Plus tard
        </Button>
        <Button size="sm" onClick={onAccept}>
          Mettre à jour
        </Button>
      </div>
    );
  }
  if (phase === 'downloading' || phase === 'blocked') {
    return (
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          {phase === 'blocked' ? 'Fermer' : 'Masquer'}
        </Button>
      </div>
    );
  }
  return null;
}
